import { TableColumnSpec, TableRow } from './types';
import { RunFile, RunReading, RunReadingKind, RunSnapshot, RunStep, RunValidation, normalizeIdentifier, printValue, tidyNumbers } from './runs';
import { ROLES } from './roles';
import { v } from './schema';
import { normalizeRows } from './tableRows';

/**
 * Where a run's steps go in a test report laid out the legacy way.
 *
 * Legacy report templates share one skeleton, and each report section is
 * filled from the same top-level procedure section, because procedures are
 * authored to one convention:
 *
 *   report section           procedure section it draws from
 *   Introduction             Introduction (reference material)
 *   DUT information          Input › "Enter DUT parameters" / "DUT"
 *   Test diagram schematic   Setup / Preparation — the diagram
 *   Test setup               Setup / Preparation — the setup photos
 *   Parameters               Input (the rest); the numbers set up in Setup / Preparation
 *   Test data                Stimulus and Data Acquisition — per step
 *   Data validation          Data Validation
 *   Analysis                 Data Analysis, Analysis
 *   Conclusion               Verification, Data Verification
 *   Appendix                 Appendix; the points/data files, wherever they were recorded
 *
 * So each step goes by the top-level section it sits in, DUT parameters (by
 * the identifiers every template uses) always go under DUT information, and
 * data files to the appendix. Where a procedure names its sections otherwise,
 * the report's section map moves one, or leaves it out.
 *
 * This only sorts the run into buckets. Which buckets print, where and how is
 * the layout's business (layout.ts), and figures are numbered where they print.
 */

export const PLACEMENTS = ['introduction', 'dut', 'setup', 'parameters', 'data', 'validation', 'analysis', 'conclusion', 'appendix', 'omit'] as const;
export type Placement = (typeof PLACEMENTS)[number];


/** A report's correction of where a procedure section's steps go, for a procedure that doesn't follow the usual section names. */
export const SECTION_MAP_COLUMNS: TableColumnSpec[] = [
  { id: 'procedureSection', title: 'Procedure section', type: 'choice', width: '22rem', help: 'a top-level section of the procedure, as the run names it' },
  { id: 'reportSection', title: 'Goes to', type: 'choice', choices: [...PLACEMENTS], width: '16rem' },
];

export const sectionMapField = (options: { label?: string; help?: string; default?: TableRow[] } = {}) =>
  v.table(SECTION_MAP_COLUMNS, {
    label: options.label ?? 'Where the procedure’s sections go',
    help: options.help ?? 'Only the sections mapped here move; the rest keep their automatic place.',
    hidden: true,
    ...(options.default ? { default: () => normalizeRows(SECTION_MAP_COLUMNS, options.default) } : {}),
  });

const norm = (s: string) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** Where a step goes when nothing says otherwise, from the names of its sections. */
export function autoPlacement(step: Pick<RunStep, 'path' | 'title'>): Exclude<Placement, 'omit'> {
  const top = norm(step.path[0] ?? '');
  const below = norm([...step.path.slice(1), step.title].join(' '));
  if (/\binput\b|\binputs\b|\bconfigur/.test(top)) return /\bdut\b|device under test/.test(below) ? 'dut' : 'parameters';
  if (/\bdut\b|device under test/.test(top)) return 'dut';
  if (/^introduc|^overview|^quick reference|^safety/.test(top)) return 'introduction';
  if (/\bsetup\b|\bset up\b|\bprepar|\bdiagram/.test(top)) return 'setup';
  if (/validat/.test(top)) return 'validation';
  if (/analy/.test(top)) return 'analysis';
  if (/verif|conclu|\bresult|evaluat|\breview/.test(top)) return 'conclusion';
  if (/^appendi|^reference|^attachment/.test(top)) return 'appendix';
  return 'data';
}

/** An instruction title as a label: "Enter the assembly name" → "Assembly name". */
export function readingLabel(title: string, identifier: string): string {
  const base = (title || identifier.replace(/_/g, ' ')).trim();
  // one instruction verb, or a chain of them ("Measure and enter the …")
  const VERB = '(?:enter|record|input|type|select|choose|provide|document|attach|upload|capture|take|measure|note)';
  const stripped = base.replace(new RegExp(`^(?:please\\s+)?${VERB}(?:\\s*(?:,|and|&)\\s*${VERB})*\\s+(?:the\\s+|a\\s+|an\\s+|your\\s+)?`, 'i'), '');
  const label = stripped.length >= 3 ? stripped : base;
  return label.charAt(0).toUpperCase() + label.slice(1);
}

const DUT_IDENTIFIERS = new Set(ROLES.filter((r) => r.group === 'DUT').flatMap((r) => r.identifiers).map(normalizeIdentifier));
const DIAGRAM = /diagram|schematic/i;

/** A reading that describes the device under test, wherever the procedure recorded it, by the identifiers every template uses. */
export const isDutReading = (r: RunReading) => DUT_IDENTIFIERS.has(normalizeIdentifier(r.identifier));

/** A DUT row, for printing each detail once: the same label and value is the same detail, wherever it was recorded. */
const dutRowKey = (r: ReadingRow) => `${norm(r.label)}\u0000${r.value.trim().toLowerCase()}`;

/** Reference material the template author attached for the operator, not something the run recorded. */
export const isReference = (r: RunReading) => r.operationType === 'File';

export interface ReadingRow {
  key: string;
  /** the reading's parameter */
  parameterId: string;
  label: string;
  /** the value as printed; empty when the reading is only files */
  value: string;
  /** a yes/no reading, printed as a check */
  flag: boolean | null;
  stepTitle: string;
  identifier: string;
  /** what the procedure made it: a measurement (`output`) or a value set up for the step */
  kind: RunReadingKind;
  /** the other steps the same value was recorded at, where a table prints it once for all of them */
  alsoAt?: string[];
}

/** A picture to print. Its figure number is given where it prints, not here. */
export interface FigureRef {
  key: string;
  fileId: string;
  fileName: string;
  caption: string;
  stepTitle: string;
}

export interface DataFileRef {
  key: string;
  fileId: string;
  fileName: string;
  kind: RunFile['kind'];
  caption: string;
  stepTitle: string;
}

/** One step of test data, validation, analysis or conclusion. */
export interface StepBlock {
  id: string;
  title: string;
  /** the sections above the step, below the top-level one */
  context: string;
  status: string;
  readings: ReadingRow[];
  /** settings left out of `readings` because they hold the value they held when last printed in this part of the report */
  unchanged: ReadingRow[];
  figures: FigureRef[];
  validations: RunValidation[];
  /** data files the step recorded, printed in the appendix */
  files: DataFileRef[];
  /** the step itself, for blocks that read it (a results matrix) */
  step: RunStep;
}

export interface ProcedureSectionPlacement {
  section: string;
  steps: number;
  auto: Exclude<Placement, 'omit'>;
  placed: Placement;
}

export interface PlacedRun {
  introduction: { readings: ReadingRow[]; figures: FigureRef[] };
  dut: { readings: ReadingRow[]; figures: FigureRef[] };
  diagram: FigureRef[];
  setup: FigureRef[];
  parameters: ReadingRow[];
  data: StepBlock[];
  validation: StepBlock[];
  analysis: StepBlock[];
  conclusion: StepBlock[];
  appendix: { blocks: StepBlock[]; figures: FigureRef[] };
  /** every data file, in step order */
  files: DataFileRef[];
  /** where each top-level procedure section went */
  placements: ProcedureSectionPlacement[];
}

export type StepBucket = 'data' | 'validation' | 'analysis' | 'conclusion' | 'appendix';
export type FigureBucket = 'introduction' | 'dut' | 'diagram' | 'setup' | 'appendix';
export type ReadingBucket = 'introduction' | 'dut' | 'parameters';

export function stepsIn(placed: PlacedRun, bucket: StepBucket): StepBlock[] {
  return bucket === 'appendix' ? placed.appendix.blocks : placed[bucket];
}

export function figuresIn(placed: PlacedRun, bucket: FigureBucket): FigureRef[] {
  if (bucket === 'introduction' || bucket === 'dut') return placed[bucket].figures;
  if (bucket === 'appendix') return placed.appendix.figures;
  return placed[bucket];
}

export function readingsIn(placed: PlacedRun, bucket: ReadingBucket): ReadingRow[] {
  return bucket === 'parameters' ? placed.parameters : placed[bucket].readings;
}

// ── printing a value once ───────────────────────────────────────────────────

/**
 * What a row records, for telling a repeat from a new value: the parameter
 * (a step input `input_x` and the operation field `x` it feeds are the same
 * setting) and its value.
 */
const settingOf = (r: ReadingRow) => normalizeIdentifier(r.identifier).replace(/^input_/, '') || norm(r.label);
const valueOf = (r: ReadingRow) => r.value.trim().toLowerCase();
/** A measurement, or a check that failed, always prints; anything else is a setting. */
const isSetting = (r: ReadingRow) => r.kind !== 'output' && r.flag !== false;

/**
 * Within each step, a value recorded twice (a step input and the field it
 * feeds) prints once; across the steps of one part of the report, a setting
 * that still holds the value it had when last printed moves to `unchanged`,
 * so each step's table shows what the step measured and what it changed.
 */
export function collapseSettings(blocks: StepBlock[]): void {
  const lastPrinted = new Map<string, string>();
  for (const b of blocks) {
    const inStep = new Set<string>();
    const readings: ReadingRow[] = [];
    const unchanged: ReadingRow[] = [];
    for (const r of b.readings) {
      const key = `${settingOf(r)}\u0000${valueOf(r)}`;
      if (inStep.has(key)) continue;
      inStep.add(key);
      if (isSetting(r) && lastPrinted.get(settingOf(r)) === valueOf(r)) {
        unchanged.push(r);
        continue;
      }
      readings.push(r);
      if (isSetting(r)) lastPrinted.set(settingOf(r), valueOf(r));
    }
    b.readings = readings;
    b.unchanged = unchanged;
  }
}

/** One row for a setting recorded with the same value at several steps, naming the others in `alsoAt`. */
export function mergeRepeated(rows: ReadingRow[]): ReadingRow[] {
  const first = new Map<string, ReadingRow>();
  const out: ReadingRow[] = [];
  for (const r of rows) {
    const key = `${settingOf(r)}\u0000${valueOf(r)}`;
    const kept = isSetting(r) ? first.get(key) : undefined;
    if (!kept) {
      const row = { ...r, alsoAt: [] as string[] };
      if (isSetting(r)) first.set(key, row);
      out.push(row);
    } else if (r.stepTitle !== kept.stepTitle && !kept.alsoAt?.includes(r.stepTitle)) {
      kept.alsoAt?.push(r.stepTitle);
    }
  }
  return out;
}

const FLAG_TRUE = new Set(['true', 'yes', 'pass', 'passed']);
const FLAG_FALSE = new Set(['false', 'no', 'fail', 'failed']);

function readingRow(reading: RunReading, step: RunStep, index: number): ReadingRow {
  const fileNames = new Set([...(reading.files ?? []), ...(reading.attachments ?? []).map((a) => a.fileName)]);
  const values = reading.values.filter((x) => x !== null && x !== '').filter((x) => !fileNames.has(String(x))).map(printValue);
  const records = reading.records.map((r) => Object.entries(r).filter(([, x]) => x !== null && x !== '').map(([k, x]) => `${k}: ${printValue(x)}`).join(', ')).filter(Boolean);
  const printed = (records.length ? records : values).join('; ');
  const single = records.length === 0 && values.length === 1 ? values[0].toLowerCase() : '';
  const flag = FLAG_TRUE.has(single) ? true : FLAG_FALSE.has(single) ? false : null;
  return { key: `${step.id}-${reading.parameterId}-${index}`, parameterId: reading.parameterId, label: readingLabel(reading.title, reading.identifier), value: printed, flag, stepTitle: step.title, identifier: reading.identifier, kind: reading.kind };
}

const ordered = (readings: RunReading[]) => readings.slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

const holdsSomething = (r: RunReading) => r.values.some((x) => x !== null && x !== '') || (r.attachments ?? []).length > 0;

/**
 * The readings of a step that print: a field whose value fills another
 * parameter of the same step (an operator's "Enter the application name"
 * filling `application_name`) is left out, since the parameter it fills
 * prints the same value under its own name.
 */
export function printedReadings(readings: RunReading[]): RunReading[] {
  const filling = new Set(readings.filter((r) => r.from && holdsSomething(r)).map((r) => r.from as string));
  return readings.filter((r) => !filling.has(r.parameterId));
}

function overridesOf(sectionMap: TableRow[]): Map<string, Placement> {
  const map = new Map<string, Placement>();
  for (const row of sectionMap) {
    const section = typeof row.procedureSection === 'string' ? norm(row.procedureSection) : '';
    const target = row.reportSection;
    if (section && typeof target === 'string' && (PLACEMENTS as readonly string[]).includes(target)) map.set(section, target as Placement);
  }
  return map;
}

/** Sort a run into the buckets a legacy-shaped report prints from. Pure. */
export function placeRun(run: RunSnapshot, sectionMap: TableRow[] = []): PlacedRun {
  const overrides = overridesOf(sectionMap);
  const files: DataFileRef[] = [];
  const out: PlacedRun = {
    introduction: { readings: [], figures: [] },
    dut: { readings: [], figures: [] },
    diagram: [],
    setup: [],
    parameters: [],
    data: [],
    validation: [],
    analysis: [],
    conclusion: [],
    appendix: { blocks: [], figures: [] },
    files,
    placements: [],
  };
  const placements = new Map<string, ProcedureSectionPlacement>();
  // a file prints once, where it is first placed: a step that reads an earlier step's capture doesn't print it again
  const placedFiles = new Set<string>();

  /** A step's readings: printed rows, figures, and the data files that go to the appendix. */
  // Reference material (a template's example diagram, an instruction photo) is kept only where the legacy
  // templates showed it — the introduction and the test diagram; elsewhere the report shows what the run recorded.
  const split = (readings: RunReading[], step: RunStep, figureFor?: (reading: RunReading, file: RunFile) => 'diagram' | 'figure', keepReference = false) => {
    const rows: ReadingRow[] = [];
    const figs: FigureRef[] = [];
    const diagrams: FigureRef[] = [];
    const stepFiles: DataFileRef[] = [];
    ordered(readings).forEach((reading, i) => {
      const row = readingRow(reading, step, i);
      const reference = isReference(reading);
      if (row.value && !reference) rows.push(row);
      for (const file of reading.attachments ?? []) {
        const kind = figureFor?.(reading, file) ?? 'figure';
        if (reference && !keepReference && kind !== 'diagram') continue;
        // the same template asset attached in several places (an example diagram) is a copy per place: one by name
        const keys = reference || kind === 'diagram' ? [file.fileId, `name:${file.fileName}`] : [file.fileId];
        if (keys.some((k) => placedFiles.has(k))) continue;
        keys.forEach((k) => placedFiles.add(k));
        const key = `${step.id}-${reading.parameterId}-${file.fileId}`;
        if (file.kind === 'image') {
          (kind === 'diagram' ? diagrams : figs).push({ key, fileId: file.fileId, fileName: file.fileName, caption: row.label, stepTitle: step.title });
        } else if (!reference) {
          const entry = { key, fileId: file.fileId, fileName: file.fileName, kind: file.kind, caption: row.label, stepTitle: step.title };
          stepFiles.push(entry);
          files.push(entry);
        }
      }
    });
    return { rows, figs, diagrams, files: stepFiles };
  };
  const block = (step: RunStep, rows: ReadingRow[], figs: FigureRef[], stepFiles: DataFileRef[]): StepBlock => ({
    id: step.id, title: step.title, context: step.path.slice(1).join(' › '), status: step.status, readings: rows, unchanged: [], figures: figs, validations: (step.validations ?? []).map((v) => ({ ...v, message: tidyNumbers(v.message) })), files: stepFiles, step,
  });

  for (const step of run.steps) {
    const sectionName = step.path[0] ?? '';
    const auto = autoPlacement(step);
    const placed: Placement = overrides.get(norm(sectionName)) ?? auto;
    const placement = placements.get(sectionName) ?? { section: sectionName, steps: 0, auto, placed };
    placement.steps += 1;
    placements.set(sectionName, placement);
    if (placed === 'omit') continue;

    // DUT parameters belong under DUT information wherever the procedure recorded them
    const readings = printedReadings(step.readings);
    const dutReadings = placed === 'dut' ? readings : readings.filter(isDutReading);
    if (dutReadings.length) {
      const { rows, figs } = split(dutReadings, step);
      // a procedure often asks for the DUT's details in more than one place (a scenario each, a step per side): print each once
      const seen = new Set(out.dut.readings.map(dutRowKey));
      out.dut.readings.push(...rows.filter((r) => !seen.has(dutRowKey(r))));
      out.dut.figures.push(...figs);
    }
    if (placed === 'dut') continue;
    const rest = readings.filter((r) => !isDutReading(r));

    if (placed === 'introduction') {
      const { rows, figs } = split(rest, step, undefined, true);
      out.introduction.readings.push(...rows);
      out.introduction.figures.push(...figs);
    } else if (placed === 'setup' || placed === 'parameters') {
      // numbers set up for the test are its parameters; its pictures are the diagram or the setup
      const isDiagram = (reading: RunReading, file: RunFile) => (DIAGRAM.test(`${reading.title} ${reading.identifier} ${file.fileName}`) || DIAGRAM.test(step.title) ? 'diagram' : 'figure');
      const { rows, figs, diagrams } = split(rest, step, isDiagram);
      out.parameters.push(...rows);
      out.setup.push(...figs);
      out.diagram.push(...diagrams);
    } else if (placed === 'appendix') {
      const { rows, figs, files: stepFiles } = split(rest, step);
      out.appendix.figures.push(...figs);
      if (rows.length || stepFiles.length) out.appendix.blocks.push(block(step, rows, [], stepFiles));
    } else {
      const { rows, figs, files: stepFiles } = split(rest, step);
      if (rows.length === 0 && figs.length === 0 && stepFiles.length === 0 && (step.validations ?? []).length === 0) continue;
      out[placed].push(block(step, rows, figs, stepFiles));
    }
  }
  // where no step recorded a diagram, the procedure's own prints (one the template carries for the whole run)
  if (out.diagram.length === 0) {
    for (const p of run.parameters ?? []) {
      for (const file of p.attachments ?? []) {
        if (file.kind !== 'image' || !DIAGRAM.test(`${p.title} ${p.identifier} ${file.fileName}`) || placedFiles.has(file.fileId)) continue;
        placedFiles.add(file.fileId);
        out.diagram.push({ key: `procedure-${p.parameterId}-${file.fileId}`, fileId: file.fileId, fileName: file.fileName, caption: readingLabel(p.title, p.identifier), stepTitle: 'Procedure' });
      }
    }
  }
  out.placements = Array.from(placements.values());
  out.parameters = mergeRepeated(out.parameters);
  for (const blocks of [out.data, out.validation, out.analysis, out.conclusion, out.appendix.blocks]) collapseSettings(blocks);
  return out;
}
